import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";
import { getCached, getCachedAt, setCached } from "../utils/wsCache";

const TYPE = "cms_get_financial_reconciliation";
const RECORDS_TYPE = "cms_get_financial_reconciliation_records";
// the first load of a long range pages through Razorpay, which can take a while
export const FINANCE_TIMEOUT_MS = 120000;

// Daily money flow for [fromDate, toDate] (IST dates, YYYY-MM-DD). Tetra only:
// the server refuses other groups.
export default function useFinancialReconciliation(fromDate, toDate) {
  const { send, isConnected } = useWebSocketContext();
  const cacheKey = `finance:${fromDate}:${toDate}`;
  const [data, setData] = useState(() => getCached(cacheKey) || null);
  const [isLoading, setIsLoading] = useState(() => !getCached(cacheKey));
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(() => getCachedAt(cacheKey));
  const requestRef = useRef(0);

  const load = useCallback(
    async ({ force = false } = {}) => {
      if (!fromDate || !toDate) return;
      const cached = getCached(cacheKey);
      if (cached && !force) {
        setData(cached);
        setLastUpdated(getCachedAt(cacheKey));
        setError("");
        setIsLoading(false);
        return;
      }
      if (!force) setData(null); // never show another range's numbers under this range
      if (!isConnected) return;

      const id = ++requestRef.current;
      setIsLoading(true);
      setError("");
      try {
        const response = await send(TYPE, { from_date: fromDate, to_date: toDate }, { timeoutMs: FINANCE_TIMEOUT_MS });
        if (id !== requestRef.current) return;
        if (!response?.success || !response.data) {
          throw new Error(response?.message || "Couldn't load the reconciliation");
        }
        // keep only complete results; a Razorpay outage should be retried next time
        if (response.data.razorpay?.available) setCached(cacheKey, response.data);
        setData(response.data);
        setLastUpdated(new Date());
      } catch (err) {
        if (id === requestRef.current) setError(err.message || "Couldn't load the reconciliation");
      } finally {
        if (id === requestRef.current) setIsLoading(false);
      }
    },
    [cacheKey, fromDate, toDate, isConnected, send]
  );

  useEffect(() => {
    load();
  }, [load]);

  return { data, isLoading, error, lastUpdated, refetch: () => load({ force: true }) };
}

// The records behind one figure: kind = received | refunds | settlements.
export function useFinancialRecordsLoader() {
  const { send } = useWebSocketContext();
  return useCallback(
    async ({ kind, fromDate, toDate, date, groupId }) => {
      const payload = { kind, from_date: fromDate, to_date: toDate };
      if (date) payload.date = date;
      if (groupId) payload.group_id = groupId;
      const response = await send(RECORDS_TYPE, payload, { timeoutMs: FINANCE_TIMEOUT_MS });
      if (!response?.success) throw new Error(response?.message || "Couldn't load the records");
      return { records: response.data, truncated: !!response.truncated };
    },
    [send]
  );
}
